import { mobileDashboardHeaderSx, mobileDashboardActionsSx } from "./dashboardMobileLayout";
import { ArrowForwardOutlined, EventRepeatOutlined, ExpandMore, RefreshOutlined,
  VisibilityOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Stack, Tab, Tabs,
  TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { issueReplenishmentExecution, loadReplenishmentExecutions,
  loadReplenishmentDocument, loadReplenishmentProposal,
  recordReplenishmentPurchaseOrder, ReplenishmentExecution,
  ReplenishmentProposal } from "./api/product";
import { HomeDashboardSelector, HomeDashboardView } from "./HomeDashboardSelector";

interface Props {
  canExecute: boolean;
  onChangeView: (view: HomeDashboardView) => void;
  operationsLabel?: string;
  showStockRisk?: boolean;
  showStockRiskV2?: boolean;
  showExpiryRisk?: boolean;
  initialProposalId?: string | null;
}

export function ReplenishmentFollowUpDashboard({
  canExecute, onChangeView, operationsLabel, showStockRisk = true,
  showStockRiskV2 = false, showExpiryRisk = false, initialProposalId = null
}: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ReplenishmentExecution | null>(null);
  const [reference, setReference] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [documentError, setDocumentError] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<"progress" | "completed">("progress");
  const [proposalDetail, setProposalDetail] = useState<ReplenishmentProposal | null>(null);
  const executions = useQuery({ queryKey: ["replenishment-executions"],
    queryFn: loadReplenishmentExecutions, refetchInterval: 60_000 });
  const proposalReview = useMutation({
    mutationFn: loadReplenishmentProposal,
    onSuccess: setProposalDetail
  });
  const updatedTime = new Date(executions.dataUpdatedAt || Date.now()).toLocaleTimeString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", {
      hour: "2-digit", minute: "2-digit"
    });
  const issue = useMutation({
    mutationFn: () => issueReplenishmentExecution(selected!.id, {
      externalReference: reference.trim() || null, expectedAt: expectedAt
        ? new Date(expectedAt).toISOString() : null, version: selected!.version
    }),
    onSuccess: async () => { setSelected(null); await queryClient.invalidateQueries({
      queryKey: ["replenishment-executions"] }); }
  });
  const recordPurchaseOrder = useMutation({
    mutationFn: () => recordReplenishmentPurchaseOrder(selected!.id, {
      purchaseOrderReference: reference.trim(), expectedAt: expectedAt
        ? new Date(expectedAt).toISOString() : null, version: selected!.version
    }),
    onSuccess: async () => { setSelected(null); await queryClient.invalidateQueries({
      queryKey: ["replenishment-executions"] }); }
  });
  const allRows = executions.data ?? [];
  const targetExecutionId = allRows.find((row) => row.proposalId === initialProposalId)?.id;
  useEffect(() => {
    if (!targetExecutionId) return;
    setTab("progress");
    setExpanded((current) => {
      if (current.has(targetExecutionId)) return current;
      return new Set(current).add(targetExecutionId);
    });
  }, [targetExecutionId]);
  const rows = allRows.filter((row) => tab === "completed"
    ? ["CLOSED", "CANCELLED"].includes(row.status)
    : !["CLOSED", "CANCELLED"].includes(row.status));
  const progressCount = allRows.filter((row) => !["CLOSED", "CANCELLED"].includes(row.status)).length;
  const completedCount = allRows.filter((row) => ["CLOSED", "CANCELLED"].includes(row.status)).length;
  const waitingRows = allRows.filter((row) => row.status === "APPROVED");
  const externalPoRows = allRows.filter((row) => row.status === "PURCHASE_REQUEST_ISSUED");
  const supplierRows = allRows.filter((row) => row.status === "ORDERED");
  const receiptRows = allRows.filter((row) => ["ACKNOWLEDGED", "ASN_CONFIRMED"].includes(row.status));
  const putawayRows = allRows.filter((row) => row.status === "RECEIVED");
  const overdueRows = allRows.filter((row) => row.overdue);
  const oldestDays = (items: ReplenishmentExecution[]) => items.length
    ? Math.max(...items.map((row) => Math.max(0, Math.floor(
      (Date.now() - new Date(row.updatedAt).getTime()) / 86_400_000)))) : null;
  const nextReceipt = receiptRows.map((row) => row.expectedAt).filter(Boolean)
    .sort()[0] ?? null;
  const putawayDone = putawayRows.reduce((sum, row) => sum + Number(row.putawayQuantity), 0);
  const putawayTotal = putawayRows.reduce((sum, row) => sum + Number(row.receivedQuantity), 0);
  const maximumDelay = overdueRows.length ? Math.max(...overdueRows.map((row) => row.expectedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(row.expectedAt).getTime()) / 86_400_000)) : 0)) : null;
  const formatDate = (value: string | null) => value ? new Date(value).toLocaleString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR") : "—";
  const openDocument = async (row: ReplenishmentExecution) => {
    const preview = window.open("", "_blank");
    setDocumentError(false);
    try {
      const blob = await loadReplenishmentDocument(row.id);
      const url = URL.createObjectURL(blob);
      if (preview) preview.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      preview?.close();
      setDocumentError(true);
    }
  };
  return <Stack spacing={1.25} sx={{ width: "100%", "@media (max-width: 599.95px)": { "& > *": { flexShrink: 0 } }, height: { xs: "auto", sm: "100%" }, minHeight: 0 }}>
    <Stack sx={mobileDashboardHeaderSx} direction="row" justifyContent="space-between" alignItems="center">
      <Box><Typography variant="h4" fontWeight={850}>{t("dashboard.replenishment.title")}</Typography>
        <Typography variant="caption" color="text.secondary">{t("dashboard.replenishment.subtitle")}</Typography></Box>
      <Stack sx={mobileDashboardActionsSx} direction="row" alignItems="center" spacing={1}><HomeDashboardSelector value="replenishment"
        operationsLabel={operationsLabel} showStockRisk={showStockRisk}
        showStockRiskV2={showStockRiskV2} showExpiryRisk={showExpiryRisk}
        onChange={(view) => view !== "replenishment" && onChangeView(view)} />
        <IconButton size="small" aria-label={t("dashboard.refresh")}
          onClick={() => void executions.refetch()}><RefreshOutlined /></IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t("dashboard.updated", { time: updatedTime })}
        </Typography></Stack>
    </Stack>
    <Grid container spacing={1.25}>{[
      { key: "waiting", value: waitingRows.length, detail: oldestDays(waitingRows) == null ? "—"
        : t("dashboard.replenishment.metrics.oldest", { count: oldestDays(waitingRows) }) },
      { key: "externalPo", value: externalPoRows.length, detail: oldestDays(externalPoRows) == null ? "—"
        : t("dashboard.replenishment.metrics.oldest", { count: oldestDays(externalPoRows) }) },
      { key: "supplier", value: supplierRows.length, detail: oldestDays(supplierRows) == null ? "—"
        : t("dashboard.replenishment.metrics.oldest", { count: oldestDays(supplierRows) }) },
      { key: "receipt", value: receiptRows.length, detail: nextReceipt
        ? t("dashboard.replenishment.metrics.next", { date: formatDate(nextReceipt) }) : "—" },
      { key: "putaway", value: putawayRows.length, detail: putawayRows.length
        ? t("dashboard.replenishment.metrics.progress", { done: putawayDone, total: putawayTotal }) : "—" },
      { key: "overdue", value: overdueRows.length, detail: maximumDelay == null ? "—"
        : t("dashboard.replenishment.metrics.delay", { count: maximumDelay }) }
    ].map(({ key, value, detail }) => <Grid key={key} size={{ xs: 6, md: 4, lg: 2 }}><Card variant="outlined"
      sx={{ px: 1.5, py: .8, height: "100%", borderTop: 3,
        borderColor: key === "overdue" ? "error.main" : "secondary.main" }}>
      <Typography variant="h5" fontWeight={850} lineHeight={1}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" noWrap
        title={`${t(`dashboard.replenishment.metrics.${key}`)} · ${detail}`}>
        {t(`dashboard.replenishment.metrics.${key}`)} · {detail}
      </Typography>
    </Card></Grid>)}</Grid>
    {executions.isError && <Alert severity="error">{t("dashboard.replenishment.loadError")}</Alert>}
    {documentError && <Alert severity="error" onClose={() => setDocumentError(false)}>
      {t("dashboard.replenishment.documentError")}</Alert>}
    {proposalReview.isError && <Alert severity="error">
      {t("dashboard.stockRisk.review.loadError")}</Alert>}
    <Tabs value={tab} onChange={(_, value) => setTab(value)}>
      <Tab value="progress" label={`${t("dashboard.replenishment.tabs.progress")} (${progressCount})`} />
      <Tab value="completed" label={`${t("dashboard.replenishment.tabs.completed")} (${completedCount})`} />
    </Tabs>
    <Card variant="outlined" sx={{ flex: 1, overflow: "auto" }}>
      {!rows.length && !executions.isLoading ? <Alert severity="info" sx={{ m: 1.5 }}>
        {t("dashboard.replenishment.empty")}</Alert> : rows.map((row) => <Box key={row.id}>
        <Box sx={{ display: "grid", minWidth: { md: 1180 },
          gridTemplateColumns: { xs: "1fr", md: "36px minmax(340px,1fr) 90px minmax(180px,230px) 180px minmax(250px,300px) 40px" },
          alignItems: "center", columnGap: 1.5, rowGap: 1, px: 1.5, py: 1.25,
          borderBottom: expanded.has(row.id) ? 0 : 1, borderColor: "divider" }}>
        <EventRepeatOutlined color={row.overdue ? "error" : "secondary"} />
        <Box sx={{ minWidth: 0 }}><Typography fontWeight={850} noWrap title={`${row.itemCode} · ${row.itemDisplayName}`}>
          {row.itemCode} · {row.itemDisplayName}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block"
            title={`${row.warehouseCode} · ${t(`dashboard.replenishment.modes.${row.executionMode}`)} · ${row.outputProfileDisplayName ?? "—"}`}>
            {row.warehouseCode} · {t(`dashboard.replenishment.modes.${row.executionMode}`)} · {row.outputProfileDisplayName ?? "—"}
          </Typography></Box>
        <Typography fontWeight={850} sx={{ whiteSpace: "nowrap" }}>{row.approvedQuantity} {row.baseUom}</Typography>
        <Box sx={{ minWidth: 0 }}><Typography variant="body2" fontWeight={800} noWrap
          title={row.primaryReference ?? undefined}>
          {row.primaryReference ?? "—"}</Typography></Box>
        <Chip color={row.overdue ? "error" : row.status === "APPROVED" ? "warning" : "primary"}
          label={row.overdue ? t("dashboard.replenishment.status.OVERDUE")
            : t(`dashboard.replenishment.status.${row.status}`)} />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
        {canExecute && row.status === "APPROVED" && <Button variant="outlined"
          endIcon={<ArrowForwardOutlined />}
          onClick={() => { setSelected(row); setReference(""); setExpectedAt(""); }}>
          {t(`dashboard.replenishment.actions.${row.executionMode}`)}</Button>}
        {canExecute && row.status === "PURCHASE_REQUEST_ISSUED" && <Button variant="outlined"
          endIcon={<ArrowForwardOutlined />}
          onClick={() => { setSelected(row); setReference("");
            setExpectedAt(row.expectedAt?.slice(0,16) ?? ""); }}>
          {t("dashboard.replenishment.actions.recordExternalPo")}</Button>}
        </Stack>
        <IconButton aria-label="details" onClick={() => setExpanded((current) => {
          const next = new Set(current); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next;
        })}><ExpandMore sx={{ transition: "transform .2s", transform: expanded.has(row.id)
          ? "rotate(180deg)" : "rotate(0deg)" }} /></IconButton>
        </Box>
        {expanded.has(row.id) && <Stack direction={{ xs: "column", md: "row" }} spacing={3}
          alignItems={{ md: "center" }} sx={{ ml: { md: "60px" }, mr: 1.5, px: 1.5, py: 1,
            bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}>
          {row.purchaseOrderReference && row.purchaseRequestReference && <Typography variant="caption">
            {t("dashboard.replenishment.prShort")}: {row.purchaseRequestReference}</Typography>}
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.replenishment.customerReferenceShort")}: {row.externalReference ?? "—"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.replenishment.expectedAt")}: {formatDate(row.expectedAt)}</Typography>
          {row.receivedQuantity > 0 && <Typography variant="caption" color="text.secondary">
            {t("dashboard.replenishment.putawayProgress", { putaway: row.putawayQuantity,
              received: row.receivedQuantity, uom: row.baseUom })}</Typography>}
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={() => proposalReview.mutate(row.proposalId)}>
            {t("dashboard.stockRisk.review.title")}</Button>
          {row.documentStatus === "SUCCEEDED" && <Button size="small" variant="outlined"
            startIcon={<VisibilityOutlined />} onClick={() => void openDocument(row)}>
            {t("dashboard.replenishment.viewPdf")}</Button>}
        </Stack>}
      </Box>)}
    </Card>
    <Dialog open={selected != null} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
      <DialogTitle>{selected && (selected.status === "PURCHASE_REQUEST_ISSUED"
        ? t("dashboard.replenishment.actions.recordExternalPo")
        : t(`dashboard.replenishment.actions.${selected.executionMode}`))}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        {selected?.executionMode === "PURCHASE_REQUEST" && selected.status === "APPROVED" &&
          <Alert severity="info">{t("dashboard.replenishment.prGeneratedHelp")}</Alert>}
        <TextField required={selected?.executionMode === "DIRECT_SUPPLIER_ORDER"
          || selected?.status === "PURCHASE_REQUEST_ISSUED"}
          label={selected?.status === "PURCHASE_REQUEST_ISSUED"
            ? t("dashboard.replenishment.purchaseOrderReference")
            : selected?.executionMode === "PURCHASE_REQUEST"
              ? t("dashboard.replenishment.customerReference")
              : t("dashboard.replenishment.purchaseOrderReference")} value={reference}
          onChange={(e) => setReference(e.target.value)} />
        <TextField type="datetime-local" label={t("dashboard.replenishment.expectedAt")}
          value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        {(issue.isError || recordPurchaseOrder.isError) && <Alert severity="error">{t("dashboard.replenishment.issueError")}</Alert>}
      </Stack></DialogContent><DialogActions><Button onClick={() => setSelected(null)}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" disabled={(selected?.executionMode === "DIRECT_SUPPLIER_ORDER"
          || selected?.status === "PURCHASE_REQUEST_ISSUED") && !reference.trim()
          || issue.isPending || recordPurchaseOrder.isPending}
          onClick={() => selected?.status === "PURCHASE_REQUEST_ISSUED"
            ? recordPurchaseOrder.mutate() : issue.mutate()}>{t("dashboard.actions.confirm")}</Button></DialogActions>
    </Dialog>
    <Dialog open={proposalDetail != null} onClose={() => setProposalDetail(null)} fullWidth maxWidth="md">
      {proposalDetail && <>
        <DialogTitle>{t("dashboard.stockRisk.review.title")} · {proposalDetail.itemCode}
          <Typography variant="body2" color="text.secondary">
            {proposalDetail.itemDisplayName} · {proposalDetail.warehouseCode}
          </Typography>
        </DialogTitle>
        <DialogContent dividers><Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" color={proposalDetail.evaluationStatus === "CRITICAL" ? "error" : "warning"}
              label={t(`dashboard.stockRisk.status.${proposalDetail.evaluationStatus}`)} />
            <Chip size="small" label={t(`dashboard.stockRisk.review.status.${proposalDetail.status}`)} />
          </Stack>
          <Grid container spacing={1.5}>{[
            ["physical", proposalDetail.onHandQuantity], ["unavailable", proposalDetail.unavailableQuantity],
            ["reserved", proposalDetail.reservedQuantity], ["available", proposalDetail.availableQuantity],
            ["inbound", proposalDetail.confirmedInboundQuantity], ["consumption", proposalDetail.averageDailyConsumption]
          ].map(([key, value]) => <Grid key={String(key)} size={{ xs: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              {t(`dashboard.stockRisk.review.fields.${key}`)}
            </Typography>
            <Typography fontWeight={800}>{Number(value)} {proposalDetail.baseUom}</Typography>
          </Grid>)}</Grid>
          <Alert severity="warning">
            {t("dashboard.stockRisk.review.recommendation", {
              quantity: `${proposalDetail.recommendedQuantity} ${proposalDetail.baseUom}`
            })}
          </Alert>
          {proposalDetail.decidedQuantity != null && <Alert
            severity={proposalDetail.status === "REJECTED" ? "error" : "success"}>
            {t(`dashboard.stockRisk.review.${proposalDetail.status === "REJECTED"
              ? "rejectedQuantity" : "approvedQuantity"}`, {
              quantity: `${proposalDetail.decidedQuantity} ${proposalDetail.baseUom}`
            })}
          </Alert>}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setProposalDetail(null)}>
          {t("common.actions.close")}</Button></DialogActions>
      </>}
    </Dialog>
  </Stack>;
}
